import { useMemo, type ComponentProps } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Divider, Link, Text } from '@customerio/pluma-components/react';
import type { SelectProps } from '@customerio/pluma-components/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import { getSegmentsQueryOptions } from '@/services/db/segment/queries';
import { requireWorkspaceId } from '@/services/db/shared/workspace';

/**
 * The pieces a segment picker is built from, shared by the two segment rows.
 *
 * There are two rows because there are two wires: one carries a single segment
 * id per condition, the other a list. What they have in common is the picker
 * itself — the same options off the same query, and the same create-segment
 * entry above them — so that much lives here and each row keeps its own state
 * shape and controls.
 */

/** One entry in a segment picker. */
export type SegmentOption = { value: string; label: string; icon: 'segment-manual' | 'segment-data-driven' };

/** The workspace's segments as picker entries, with the query's loading state. */
export type SegmentPickerOptions = { options: SegmentOption[]; isLoading: boolean };

/** The workspace's segments as picker entries, and whether they are still loading. */
export function useSegmentOptions(): SegmentPickerOptions {
  const workspaceId = requireWorkspaceId();
  const queryClient = useQueryClient();
  const segmentsQuery = useQuery(getSegmentsQueryOptions(workspaceId, queryClient));

  const options = useMemo(
    () =>
      (segmentsQuery.data?.segments ?? []).map((segment) => ({
        value: String(segment.id),
        label: segment.name,
        icon: segment.type === 'static' ? ('segment-manual' as const) : ('segment-data-driven' as const),
      })),
    [segmentsQuery.data],
  );

  return { options, isLoading: segmentsQuery.isLoading };
}

// Pluma renders this above the option list, below the search input. Its props
// come from the Select's own slot type, so the unexported select-context shape
// never has to be restated here.
type BeforeOptionsProps = ComponentProps<NonNullable<SelectProps['beforeOptionsComponent']>>;

const createSegmentLinkStyle = {
  borderRadius: 0,
  outlineOffset: `calc(${themeVars.border['width-025']} * -1)`,
};

type CreateSegmentEntryProps = BeforeOptionsProps & {
  onCreateSegment: () => void;
  testId: string;
};

/** The picker's "create a new segment" entry, above the segment options. */
function CreateSegmentEntry({ selectContext, onCreateSegment, testId }: CreateSegmentEntryProps) {
  return (
    <Text display="flex" flexDirection="column" size="sm">
      <Link
        data-testid={testId}
        onClick={() => {
          // Close the dropdown before the form opens, so the menu isn't left
          // floating over it.
          selectContext.floatingContext.onOpenChange(false);
          onCreateSegment();
        }}
        px="100"
        py="050"
        flexGrow="1"
        textAlign="left"
        style={createSegmentLinkStyle}
      >
        Create a new data-driven segment
      </Link>
      <Divider />
    </Text>
  );
}

/**
 * The picker's create-segment entry for one row, or undefined where the
 * shortcut is not offered — which leaves the entry out of the picker entirely.
 *
 * Memoized on the handler and id so an open dropdown is not remounted on every
 * render of the row above it.
 */
export function useCreateSegmentEntry(
  onCreateSegment: ((nodeId: string) => void) | undefined,
  nodeId: string,
  testId: string,
): SelectProps['beforeOptionsComponent'] {
  return useMemo(
    () =>
      onCreateSegment == null
        ? undefined
        : function SegmentCreateEntry(props: BeforeOptionsProps) {
            return <CreateSegmentEntry {...props} onCreateSegment={() => onCreateSegment(nodeId)} testId={testId} />;
          },
    [onCreateSegment, nodeId, testId],
  );
}
